import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../../config/api';
import { isPasswordValid } from '../../utils/authValidation';
import { Eyebrow } from '../Layout/PageLayout';
import {
  AuthPage,
  AuthLayout,
  AuthCard,
  CardBody,
  CardFoot,
  PanelTitle,
  PanelText,
  Form,
  Field,
  Input,
  FieldError,
  MessageBox,
  PrimaryButton,
  PasswordChips,
  InlineLink,
} from './AuthSharedStyles';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const mismatch = confirmPassword.length > 0 && !passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!token) {
      setError('Reset token is missing.');
      return;
    }

    if (!isPasswordValid(password)) {
      setError('Password does not meet all requirements.');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to reset password');

      setMessage('Password updated successfully. You can sign in now.');
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPage>
      <AuthLayout $centered>
        <AuthCard>
          <CardBody>
            <Eyebrow style={{ marginBottom: '0.625rem' }}>Account recovery</Eyebrow>
            <PanelTitle>Set a new password</PanelTitle>
            <PanelText>{email ? `Choose a new password for ${email}.` : 'Choose your new password.'}</PanelText>

            {error && (
              <MessageBox $error role="alert">
                {error}
              </MessageBox>
            )}
            {message && <MessageBox role="status">{message}</MessageBox>}

            <Form onSubmit={handleSubmit}>
              <Field>
                <label htmlFor="rp-password">New password</label>
                <Input
                  id="rp-password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  aria-describedby="rp-reqs"
                  required
                />
              </Field>
              <PasswordChips id="rp-reqs" password={password} />
              <Field>
                <label htmlFor="rp-confirm">Confirm new password</label>
                <Input
                  id="rp-confirm"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  $invalid={mismatch}
                  required
                />
                {mismatch && <FieldError>The passwords do not match.</FieldError>}
              </Field>
              <PrimaryButton type="submit" disabled={loading}>
                {loading ? 'Updating password…' : 'Reset password'}
              </PrimaryButton>
            </Form>
          </CardBody>
          <CardFoot>
            Back to <InlineLink to="/login">login</InlineLink>
          </CardFoot>
        </AuthCard>
      </AuthLayout>
    </AuthPage>
  );
};

export default ResetPassword;
